import classNames from 'classnames';
import React, { useContext, useEffect } from 'react';
import { requestCartItems, resetCart, updateCart } from 'common/features/store/duck/cart/duck';
import { useDispatch, useSelector } from 'react-redux';
import { CartInfoState, OrderSteps } from './CartInfoState';
import { Breadcrumb } from './components/Breadcrumb';
import { getBreadcrumbPath } from './utils';
import { CartInfoDetails } from './utils/useCartDetails';
import { OrderSummaryDetails } from './components/OrderSummaryDetails';
import { OrderSummaryDetailsMobile } from './components/OrderSummaryDetailsMobile';

import { getPath, Routes } from 'routes';
import { ShopScreen } from 'features/shop-base/ShopScreen';
import { isDefined } from 'common/utils';
import { createStyles } from 'utils/createStyle';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';
import { FincenFlowView } from 'features/order-summary/components/fincen/FincenFlowView';
import { BrandCardTiles } from './components/BrandCardTiles';
import { FeatureFlag } from 'components/feature-flag';
import { OrderSuccessGridData } from './components/success/OrderSuccessGrid';

import { useHistory, useLocation } from 'react-router-dom';
import { PaymentIframe } from './components/PaymentIframe';
import { getPaymentWindowUrl, isInIframe, mainWebsiteOrigin, paymentWebsiteOrigin } from 'utils';
import {
    gaLogBeginCheckout,
    gaLogPurchase,
    logCardEnrolledLocalDining,
    logPageView,
    logViewedCheckout,
} from 'config/analytics';
import { sendToLoginPage } from 'features/auth/components/withAuth';
import { useBlackBoxValues } from 'modules/fraud-force/fraudForce';
import { cloDidNotLink } from 'common/api/e-comm/orders/service';
import { selectUserId } from 'common/features/store/duck/account/duck';

export interface OrderScreenProps {
    title: string;
    className?: string;
    hideHeader?: boolean;
    orderStep: OrderSteps;
    showSummary: boolean;
    showMobileSummary: boolean;
    noMaxWidth?: boolean;
    showBrandCardTiles?: boolean;
    embedView?: boolean;
}

export interface OrderScreenContextType {
    cartInfo: CartInfoState;
    cartInfoDetails: CartInfoDetails;
    setCartInfo: (value: CartInfoState) => void;
}

const enabledRules: {
    [key in OrderSteps]: (
        cartInfoState: CartInfoState,
        cartInfoDetails: CartInfoDetails
    ) => boolean;
} = {
    summary: () => true,
    payment: (cartInfo) => isDefined(cartInfo.paymentMethodId),
    shipping: (cartInfo, cartInfoDetails) =>
        Boolean(cartInfoDetails.shipToHomeIfPossible) && Boolean(cartInfo.paymentMethodId),
    review: (cartInfo, cartInfoDetails) =>
        Boolean(cartInfo.paymentMethodId) &&
        (!cartInfoDetails.shipToHomeIfPossible || Boolean(cartInfo.shippingMethodId)),
    success: () => false,
};

const styles = createStyles({
    orderDetails: [
        'max-w-lg xl:max-w-2xl w-full ml-auto py-12 hidden lg:block',
        {
            right: '0px',
            backgroundColor: '#F7F7F7',
            zIndex: -1,
        },
    ],
});

export const OrderScreenListener = () => {
    const history = useHistory();
    const dispatch = useDispatch<any>();

    const { retrieveBlackBox } = useBlackBoxValues(!isInIframe());
    const userId = useSelector(selectUserId);

    useEffect(() => {
        const handleMessage = (event: any) => {
            // Always check the origin of the message

            if (isInIframe()) {
                if (event.origin !== mainWebsiteOrigin()) {
                    return;
                }

                if (event.data.type === 'cartUpdated') {
                    if (event.data.cart.lineItems.length === 0) {
                        dispatch(resetCart());
                    } else {
                        dispatch(updateCart({ cart: event.data.cart }));
                    }
                } else if (event.data.type === 'resetCart') {
                    dispatch(resetCart());
                }
            } else {
                if (event.origin !== paymentWebsiteOrigin()) {
                    return;
                }

                if (event.data.type === 'orderPlaced') {
                    const data = event.data.orderSuccessGridData as OrderSuccessGridData;
                    const linkingWithClo = event.data.linkingWithClo;
                    sessionStorage.setItem(
                        `placed_order_${data.data.orderId}`,
                        JSON.stringify(data)
                    );

                    dispatch(resetCart());

                    if (linkingWithClo) {
                        logCardEnrolledLocalDining(
                            Boolean(cloDidNotLink(data.data.errorCode)),
                            'checkout'
                        );
                    }

                    history.push(
                        getPath(Routes.ShopOrderSuccess, {
                            orderId: data.data.orderId?.toString() || 'unknown',
                        })
                    );
                } else if (event.data.type === 'cartUpdated') {
                    dispatch(requestCartItems());
                } else if (event.data.type === 'goShopping') {
                    history.push(Routes.Brands);
                } else if (event.data.type === 'logPurchase') {
                    gaLogPurchase(event.data.gaData);
                } else if (event.data.type === 'logBeginCheckout') {
                    gaLogBeginCheckout(event.data.cart);
                } else if (event.data.type === 'resetCart') {
                    dispatch(resetCart());
                } else if (event.data.type === 'scrollToTop') {
                    window.scrollTo(0, 0);
                } else if (event.data.type === 'pathChanged') {
                    window.history.replaceState(null, '', `#${event.data.path}`);
                    logPageView({
                        pagePath: window.location.pathname + window.location.hash,
                        pageLocation: window.location.href,
                    });
                } else if (event.data.type === 'closeOnHold') {
                    dispatch(resetCart());
                    history.push(Routes.Shop);
                } else if (event.data.type === 'signIn') {
                    sendToLoginPage();
                } else if (event.data.type === 'navigateTo') {
                    history.push(event.data.url);
                } else if (event.data.type === 'blackBoxRequest') {
                    const { requestId } = event.data;

                    retrieveBlackBox().then((response) => {
                        event.source.postMessage(
                            { requestId, response, type: 'blackBoxResponse' },
                            event.origin
                        );
                    });
                } else if (event.data.type === 'view_checkout') {
                    logViewedCheckout(event.data.action, userId);
                }
            }
        };

        // Add event listener on mount
        window.addEventListener('message', handleMessage);

        // Clean up event listener on unmount
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    });
    return <div></div>;
};

export const OrderScreen = ({
    children,
    className,
    title,
    hideHeader,
    showSummary,
    showMobileSummary,
    noMaxWidth,
    showBrandCardTiles = false,
    embedView = false,
}: React.PropsWithChildren<OrderScreenProps>) => {
    const location = useLocation();
    useScrollToTop();
    useEffect(() => {
        if (isInIframe()) {
            const pathSegments = location.pathname.split('/').filter(Boolean);
            const lastSegment = pathSegments[pathSegments.length - 1] || 'unknown';
            window.parent.postMessage(
                { type: 'pathChanged', path: lastSegment },
                mainWebsiteOrigin()
            );
        }
    }, [location]);

    const { cartInfo, cartInfoDetails } = useContext(OrderScreenContext);
    const orderBreadcrumbPath = getBreadcrumbPath(cartInfo, cartInfoDetails, enabledRules);

    const { shipToHome } = cartInfo;
    const isIframed = isInIframe();
    const shouldIframe = embedView && !isIframed;
    const iFrameSrc = getPaymentWindowUrl(window.location);

    showSummary = showSummary && isIframed;
    showMobileSummary = showMobileSummary && isIframed;
    hideHeader = hideHeader || !isIframed;
    showBrandCardTiles = showBrandCardTiles && !isIframed;

    return (
        <ShopScreen isEmbeddedView={isIframed}>
            <div className="relative flex justify-center">
                <div
                    className={classNames(
                        `w-full {!noMaxWidth && 'max-w-screen-xl'} my-0`,
                        isIframed ? 'py-4 px-6 lg:px-8 sm:py-20 xl:mx-24' : 'px-0 mx-0',
                        className
                    )}
                >
                    {!hideHeader && (
                        <div className="mb-4">
                            <div className="flex gap-3 ">
                                <h1 className="text-brand-dark text-3xl sm:text-5xl mb-6 mt-4 lg:mt-0">
                                    {title}
                                </h1>
                            </div>
                            <Breadcrumb paths={orderBreadcrumbPath} />
                        </div>
                    )}
                    <FincenFlowView />
                    {shouldIframe ? <PaymentIframe src={iFrameSrc} /> : children}
                </div>
                {showSummary && (
                    <div className={styles.orderDetails}>
                        <div className="hidden lg:block">
                            <OrderSummaryDetails />
                        </div>
                    </div>
                )}
                {showMobileSummary && (
                    <div className="lg:hidden">
                        <OrderSummaryDetailsMobile shipToHome={Boolean(shipToHome)} />
                    </div>
                )}
            </div>
            {showBrandCardTiles && (
                <FeatureFlag flagKey="enable_cart_brand_card_tiles">
                    <BrandCardTiles />
                </FeatureFlag>
            )}
            <OrderScreenListener />
        </ShopScreen>
    );
};
